// Argus — PreviewPane: inline preview pentru selected entry (image / text). Vrf split right.
// Folder selection / file fără preview → empty state. file_read fetch + display similar FilePreviewPanel modal.

import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, Alert, Link, IconButton, Tooltip, LinearProgress } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { previewKindFromName, readFilePreview } from '../../lib/fileRead';
import { createMseVideoSource, type MseVideoSource } from '../../lib/videoStream';
import { joinPath, formatEntryDisplayName, type FsEntry } from '../../lib/fsEntries';
import { formatBytes } from '../../lib/dirListing';
import { dayjs } from '../../lib/dayjsSetup';

export interface PreviewPaneProps {
  agentId: string;
  agentOnline: boolean;
  parentPath: string;
  selectedEntry: FsEntry | null;
  onExpandPreview?: (entry: FsEntry) => void;
}

interface PaneState {
  loading: boolean;
  error: string | null;
  artifactId: string | null;
  downloadUrl: string | null;
  textContent: string | null;
  forFullPath: string | null;
}

const INITIAL: PaneState = { loading: false, error: null, artifactId: null, downloadUrl: null, textContent: null, forFullPath: null };

export function PreviewPane({ agentId, agentOnline, parentPath, selectedEntry, onExpandPreview }: PreviewPaneProps): JSX.Element {
  const [state, setState] = useState<PaneState>(INITIAL);
  // Bloc ws-direct-batch-3: ref-uri pentru MSE wiring video element + tracking active source.
  const mseSourceRef = useRef<MseVideoSource | null>(null);
  const videoElRef = useRef<HTMLVideoElement | null>(null);

  const isDrive = selectedEntry?.driveType !== undefined && selectedEntry?.driveType !== null;
  const isFolder = selectedEntry?.isDir === true && !isDrive;
  const isFile = selectedEntry !== null && selectedEntry.isDir === false;
  const fullPath = isFile ? joinPath(parentPath, selectedEntry!.name) : null;
  const kind = isFile ? previewKindFromName(selectedEntry!.name) : 'unknown';

  useEffect(() => {
    if (!isFile || fullPath === null) {
      setState(INITIAL);
      return;
    }
    if (!agentOnline) {
      setState({ ...INITIAL, error: 'Agent offline — preview not available.' });
      return;
    }
    if (kind !== 'image' && kind !== 'text' && kind !== 'video') {
      // Skip auto file_read for non-previewable types — show metadata only (incl. video_unsupported).
      setState({ ...INITIAL, forFullPath: fullPath });
      return;
    }
    let cancelled = false;
    let blobUrlToRevoke: string | null = null;
    let mseSourceToDispose: MseVideoSource | null = null;
    setState({ loading: true, error: null, artifactId: null, downloadUrl: null, textContent: null, forFullPath: fullPath });
    (async () => {
      try {
        const result = await readFilePreview(agentId, fullPath, '', kind as 'text' | 'image' | 'video');
        if (cancelled) {
          if (result.downloadUrl) URL.revokeObjectURL(result.downloadUrl);
          return;
        }
        if (result.downloadUrl) blobUrlToRevoke = result.downloadUrl;
        setState({ loading: false, error: null, artifactId: null, downloadUrl: result.downloadUrl || null, textContent: result.textContent, forFullPath: fullPath });
      } catch (err) {
        if (cancelled) return;
        // Bloc ws-direct-batch-3: pentru kind=video + FILE_TOO_LARGE_FOR_WS, fallback la MSE streaming.
        // Duck-typing pe errorCode (instanceof poate eșua cross-HMR module instances).
        const isTooLarge = err !== null && err !== undefined && typeof err === 'object'
          && (err as { errorCode?: string }).errorCode === 'FILE_TOO_LARGE_FOR_WS';
        if (isTooLarge && kind === 'video') {
          console.debug('[PreviewPane] FILE_TOO_LARGE_FOR_WS for video → fallback MSE streaming');
          try {
            const mse = createMseVideoSource(agentId, fullPath, '', {
              onError: (mseError) => {
                if (cancelled) return;
                console.error('[PreviewPane] MSE streaming error:', mseError);
                setState((prev) => ({ ...prev, error: `Streaming eșuat: ${mseError.message}`, downloadUrl: null }));
              },
            });
            mseSourceToDispose = mse;
            mseSourceRef.current = mse;
            if (videoElRef.current) mse.attachVideoElement(videoElRef.current);
            setState({ loading: false, error: null, artifactId: null, downloadUrl: mse.srcUrl, textContent: null, forFullPath: fullPath });
            return;
          } catch (mseErr) {
            const mseMsg = mseErr instanceof Error ? mseErr.message : 'Streaming failed';
            console.error('[PreviewPane] MSE init failed:', mseErr);
            setState({ loading: false, error: `Streaming eșuat: ${mseMsg}`, artifactId: null, downloadUrl: null, textContent: null, forFullPath: fullPath });
            return;
          }
        }
        const message = err instanceof Error ? err.message : 'Preview failed';
        setState({ loading: false, error: message, artifactId: null, downloadUrl: null, textContent: null, forFullPath: fullPath });
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrlToRevoke !== null) URL.revokeObjectURL(blobUrlToRevoke);
      if (mseSourceToDispose !== null) {
        mseSourceToDispose.dispose();
        if (mseSourceRef.current === mseSourceToDispose) mseSourceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, fullPath, kind, agentOnline]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="overline" color="text.secondary" sx={{ flexGrow: 1, lineHeight: 1.4 }}>
          Preview
        </Typography>
        {isFile && onExpandPreview ? (
          <Tooltip title="Open full preview">
            <IconButton size="small" onClick={() => onExpandPreview(selectedEntry!)} sx={{ p: 0.5 }}>
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1.25 }}>
        {selectedEntry === null ? (
          <Typography variant="body2" color="text.secondary">
            Select an item to see its preview.
          </Typography>
        ) : isDrive ? (
          <DriveMetadata entry={selectedEntry} />
        ) : isFolder ? (
          <FolderMetadata entry={selectedEntry} parentPath={parentPath} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                {selectedEntry.name}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', display: 'block', wordBreak: 'break-all' }}>
                {fullPath}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {selectedEntry.size !== null && selectedEntry.size !== undefined ? formatBytes(selectedEntry.size) : '—'}
                {selectedEntry.modifiedAt ? ` · ${dayjs(selectedEntry.modifiedAt).format('DD MMM YYYY HH:mm')}` : ''}
              </Typography>
            </Box>

            {state.loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="caption" color="text.secondary">Reading file from agent…</Typography>
              </Box>
            ) : state.error ? (
              <Alert severity="error" variant="outlined">{state.error}</Alert>
            ) : kind === 'image' && state.downloadUrl ? (
              // Fix #12: imagine smaller than pane → original size; larger → 100% width pane.
              // CSS: width 100% scalează la pane width DOAR dacă natural width > pane width
              // (browser respectă img intrinsic dimensions atunci când max-width=100% e singura constraint).
              <Box
                component="img"
                src={state.downloadUrl}
                alt={selectedEntry.name}
                crossOrigin="use-credentials"
                sx={{ maxWidth: '100%', height: 'auto', display: 'block', borderRadius: 0.5, backgroundColor: 'rgba(0,0,0,0.3)' }}
              />
            ) : kind === 'video' && state.downloadUrl ? (
              <Box
                component="video"
                ref={(el: HTMLVideoElement | null) => {
                  videoElRef.current = el;
                  if (el && mseSourceRef.current) mseSourceRef.current.attachVideoElement(el);
                }}
                src={state.downloadUrl}
                controls
                preload="metadata"
                crossOrigin="use-credentials"
                sx={{ maxWidth: '100%', maxHeight: 360, borderRadius: 0.5, backgroundColor: 'rgba(0,0,0,0.6)', outline: 'none' }}
              />
            ) : kind === 'video_unsupported' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" color="warning.main">
                  Video format not supported in preview ({selectedEntry.name.split('.').pop()?.toLowerCase()}).
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Use Download to play locally with a media player.
                </Typography>
              </Box>
            ) : kind === 'text' && state.textContent !== null ? (
              <Box
                component="pre"
                data-allow-select="true"
                sx={{
                  m: 0, p: 1, fontSize: 11.5, fontFamily: 'monospace',
                  backgroundColor: 'action.hover', borderRadius: 0.5,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  maxHeight: 480, overflow: 'auto',
                }}
              >
                {state.textContent}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Inline preview not supported for this file type.
                </Typography>
                {state.downloadUrl ? (
                  <Link href={state.downloadUrl} target="_blank" rel="noopener" download={selectedEntry.name} sx={{ fontSize: 12 }}>
                    Download file
                  </Link>
                ) : null}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function DriveMetadata({ entry }: { entry: FsEntry }): JSX.Element {
  const free = entry.freeBytes ?? 0;
  const total = entry.totalBytes ?? 0;
  const used = total > 0 ? total - free : 0;
  const usedPct = total > 0 ? Math.min(100, Math.max(0, (used / total) * 100)) : 0;
  const progressColor: 'primary' | 'warning' | 'error' =
    usedPct >= 90 ? 'error' : usedPct >= 70 ? 'warning' : 'primary';
  const driveTypeLabel: Record<string, string> = {
    fixed: 'Local Disk',
    removable: 'Removable Disk',
    cdrom: 'CD/DVD Drive',
    network: 'Network Drive',
  };
  const typeLabel = driveTypeLabel[entry.driveType ?? ''] ?? 'Drive';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatEntryDisplayName(entry)}</Typography>
      <Typography variant="caption" color="text.secondary">{typeLabel}</Typography>
      <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>{entry.name}\</Typography>
      {total > 0 ? (
        <>
          <LinearProgress variant="determinate" value={usedPct} color={progressColor} sx={{ height: 8, borderRadius: 1, mt: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {formatBytes(free)} free of {formatBytes(total)} ({Math.round(usedPct)}% used)
          </Typography>
        </>
      ) : (
        <Typography variant="caption" color="text.disabled">Capacity unknown</Typography>
      )}
    </Box>
  );
}

function FolderMetadata({ entry, parentPath }: { entry: FsEntry; parentPath: string }): JSX.Element {
  const fullPath = joinPath(parentPath, entry.name);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{entry.name}</Typography>
      <Typography variant="caption" color="text.secondary">Folder</Typography>
      <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
        {fullPath}
      </Typography>
      {entry.modifiedAt ? (
        <Typography variant="caption" color="text.secondary">
          Modified {dayjs(entry.modifiedAt).format('DD MMM YYYY HH:mm')}
        </Typography>
      ) : null}
      {entry.isHidden ? <Typography variant="caption" color="warning.main">Hidden</Typography> : null}
      {entry.isSystem ? <Typography variant="caption" color="warning.main">System</Typography> : null}
    </Box>
  );
}
